import MaterialReactTable, { MaterialReactTableProps } from 'material-react-table'
import Box from '@mui/material/Box'
import { useMediaQuery } from '@mui/material'
import Pagination from './pagination'
import OpenCloseArrowIcon from '../../icons/openCloseArrowIcon'
const ExpandComponent = ({ row }: any) => (
  <OpenCloseArrowIcon
    sx={{
      mr: '19px',
      cursor: 'pointer',
      p: '5px',
    }}
    open={row.getIsExpanded()}
  />
)
interface CustomTableProps<T extends Record<string, any>> extends MaterialReactTableProps<T> {
  propsPagination?: React.ComponentProps<typeof Pagination>
}
const CustomTable = <T extends Record<string, any>>({
  displayColumnDefOptions,
  propsPagination,
  data,
  ...props
}: CustomTableProps<T>) => {
  const isMobile = useMediaQuery('(max-width: 800px)')
  const isTablet = useMediaQuery('(max-width: 1200px)')
  const hasData = data && data.length > 0
  const shouldScroll = isMobile || isTablet

  return (
    <>
      <Box 
        sx={{ 
          width: '100%',
          ...(shouldScroll && {
            overflowX: 'auto',
            '&::-webkit-scrollbar': {
              height: '8px',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: '#666666',
              borderRadius: '4px',
              '&:hover': {
                backgroundColor: '#555555',
              },
              '&:active': {
                backgroundColor: '#444444',
              },
            },
          }),
        }}
      >
        <MaterialReactTable
          layoutMode='grid'
          muiTablePaperProps={{
            sx: { 
              borderRadius: 'none', 
              boxShadow: 'none',
              ...(shouldScroll && {
                minWidth: '930px',
                '&::-webkit-scrollbar': {
                  display: 'none',
                },
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
              }),
            },
          }}
          muiTableContainerProps={{
            sx: {
              ...(shouldScroll && {
                '&::-webkit-scrollbar': {
                  display: 'none',
                },
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
              }),
            },
          }}
          muiTableHeadCellProps={{
            sx: {
              color: 'white',
              textTransform: 'uppercase',
              fontSize: isMobile ? '12px' : '14px',
              lineHeight: isMobile ? '16px' : '18px',
              fontWeight: '500',
              letterSpacing: '1px',
              justifyContent:'center',
              py: isMobile ? '8px' : '11px',
              px: (isMobile || isTablet) ? '8px' : '0',
              whiteSpace: 'nowrap',
              '& .MuiTableSortLabel-root': {
                ml: (isMobile || isTablet) ? '8px' : '14px',
                mr: (isMobile || isTablet) ? '8px' : '0',
                minWidth: (isMobile || isTablet) ? '24px' : 'auto',
              },
              '& .MuiTableSortLabel-root.Mui-active .MuiTableSortLabel-icon, & .MuiTableSortLabel-root:hover .MuiTableSortLabel-icon':
                {
                  color: '#A5A3A3',
                },
              '& .MuiTableSortLabel-icon': {
                margin: (isMobile || isTablet) ? '0 4px' : '0',
              },
            },
          }}
          muiTableHeadRowProps={{
            sx: { backgroundColor: 'primary.main' },
          }}
          muiTableBodyCellProps={({ row }) => ({
            sx: {
              p: isMobile ? '8px 4px' : '10px 0',
              borderBottom: '1px solid #DADCDD',
              height: isMobile ? '40px' : '44px',
              ...(hasData && {
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'unset',
              }),
              ...(row.getIsExpanded() ? { borderBottom: 'none' } : {}),
            },
          })}
          enableColumnFilters={false}
          enableTopToolbar={false}
          enablePagination={false}
          enableBottomToolbar={false}
          enableExpandAll={false}
          enableColumnActions={false}
          enableColumnResizing={true}
          positionExpandColumn='last'
          displayColumnDefOptions={{
            'mrt-row-expand': {
              size: 3,
              maxSize: 3,
              minSize: 3,
              header: '',
              muiTableBodyCellProps: ({ row }) => ({
                sx: {
                  px: '0',
                  justifyContent: 'end',
                  borderBottom: '1px solid #DADCDD',
                  ...(row.getIsExpanded() ? { borderBottom: 'none' } : {}),
                },
              }),
              Cell: ExpandComponent,
            },
            ...displayColumnDefOptions,
          }}
          muiTableDetailPanelProps={({ row }) => ({
            sx: {
              width: '100%',
              pt: '3px',
              pl: isMobile ? '16px' : '56px',
              pr: isMobile ? '16px' : '0',
              ...(row.getIsExpanded() ? { pb: '36px' } : {}),
            },
          })}
          localization={{
            unsorted: '',
            sortByColumnAsc: '',
            sortByColumnDesc: '',
            sortedByColumnAsc: '',
            sortedByColumnDesc: '',
            noRecordsToDisplay: 'Aucun élément à afficher',
          }}
          {...props}
          data={data || []}
        />
      </Box>
      <Box sx={{ mt: '40px', width: '100%', display: 'flex', justifyContent: 'center' }}>
        {propsPagination && propsPagination.count && propsPagination.count > 1 && (
          <Pagination 
            size={isMobile ? 'small' : isTablet ? 'medium' : 'large'} 
            {...propsPagination} 
          />
        )}
      </Box>
    </>
  )
}

export default CustomTable
